import React from "react";
import Logo from "../components/Logo";
import { CONTACT, IDENTITIES } from "../lib/siteData";
import { Mail, MapPin, Linkedin, Globe, QrCode, Download, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PROFILE = {
  name: "Karamjeet Singh Sodhi",
  shortName: "Karamjeet Sodhi",
  title: "Academic Excellence Coordinator",
  org: "CM SHRI School, Amalwas Jawalapuri",
  orgSub: "Directorate of Education, Delhi",
  credentials: [
    "TESOL International Faculty",
    "CBSE Master Trainer · KRP",
    "Core Committee Member — CBSE English (Secondary) 2026–29",
    "SUSI 2026 Educator — University of Montana",
    "Selected International Virtual Presenter — ASCD & ISTE Live 2026",
  ],
  website: "karamsodhi.com",
  websiteUrl: "https://karamsodhi.com",
};

function buildVCard() {
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${PROFILE.name}`,
    `N:Sodhi;Karamjeet;Singh;;`,
    `TITLE:${PROFILE.title}`,
    `ORG:${PROFILE.org}, ${PROFILE.orgSub}`,
    `EMAIL;TYPE=INTERNET:${CONTACT.email}`,
    `URL:${PROFILE.websiteUrl}`,
    `URL;TYPE=LinkedIn:${CONTACT.linkedin}`,
    `ADR;TYPE=WORK:;;;${CONTACT.city};;;India`,
    "NOTE:Educator · Author · Trainer · Innovator · Consultant. TESOL International Faculty · CBSE Master Trainer · SUSI 2026 Scholar.",
    "END:VCARD",
  ].join("\n");
}

export default function BusinessCard() {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
    PROFILE.websiteUrl
  )}&color=1E1B4B&bgcolor=FAF6EE&margin=0&qzone=1`;

  const downloadVCard = () => {
    const vcf = buildVCard();
    const blob = new Blob([vcf], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Karamjeet-Sodhi.vcf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Contact saved — open the file to add to your phone.");
  };

  const shareCard = async () => {
    const shareData = {
      title: `${PROFILE.shortName} — Educator · Author · Trainer`,
      text: "Connect with Karamjeet Sodhi — TESOL International Faculty, CBSE Master Trainer, SUSI 2026 Scholar.",
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied to clipboard");
      }
    } catch (e) {
      // user cancelled
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 sm:p-8 relative overflow-hidden"
      style={{ background: "#FAF6EE" }}
    >
      {/* Decorative blobs */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full opacity-[0.10] blur-3xl"
        style={{ background: "radial-gradient(circle, #D97706 0%, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -left-32 h-[480px] w-[480px] rounded-full opacity-[0.08] blur-3xl"
        style={{ background: "radial-gradient(circle, #EC4899 0%, transparent 70%)" }}
      />

      <div className="relative w-full max-w-2xl">
        {/* Card */}
        <article
          data-testid="business-card"
          className="relative bg-white shadow-[0_40px_80px_-30px_rgba(30,27,75,0.35)] print:shadow-none"
        >
          {/* Top dark band */}
          <div className="bg-ink text-ivory px-7 sm:px-10 py-6 flex items-center justify-between">
            <Logo invert />
            <p className="text-[10px] uppercase tracking-[0.28em] text-saffron">
              SUSI · 2026 · Montana
            </p>
          </div>

          {/* Body */}
          <div className="px-7 sm:px-10 py-10 grid grid-cols-1 sm:grid-cols-12 gap-8">
            <div className="sm:col-span-7">
              <p className="overline text-saffron flex items-center gap-2">
                <Sparkles size={12} /> {IDENTITIES.join(" · ")}
              </p>

              <h1
                className="mt-4 text-4xl sm:text-5xl text-ink leading-tight"
                style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 400 }}
                data-testid="card-name"
              >
                Karamjeet <span className="italic text-saffron">Singh Sodhi</span>
              </h1>

              <p className="mt-4 body-prose text-[15px] text-ink/80">
                <span className="font-medium text-ink">{PROFILE.title}</span>
                <br />
                {PROFILE.org}
                <br />
                <span className="text-ink/60">{PROFILE.orgSub}</span>
              </p>

              <div className="mt-6 pt-6 border-t border-ink-10" style={{ borderColor: "rgba(30,27,75,0.12)" }}>
                <ul className="space-y-1.5">
                  {PROFILE.credentials.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-ink/75 body-prose">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-saffron shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* QR + contact */}
            <div className="sm:col-span-5">
              <div className="border border-ink-10 p-4 bg-ivorySoft" style={{ borderColor: "rgba(30,27,75,0.18)" }}>
                <img
                  src={qrUrl}
                  alt={`QR code to ${PROFILE.website}`}
                  className="w-full aspect-square object-contain"
                  data-testid="card-qr"
                />
                <p className="mt-2 text-center text-[10px] uppercase tracking-[0.22em] text-ink/60">
                  Scan for full profile
                </p>
              </div>

              <div className="mt-5 space-y-2.5 text-[13px] body-prose">
                <a href={`mailto:${CONTACT.email}`} data-testid="card-email" className="flex items-center gap-2.5 text-ink/85 hover:text-saffron transition-colors">
                  <Mail size={13} className="text-saffron shrink-0" />
                  <span className="truncate">{CONTACT.email}</span>
                </a>
                <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" data-testid="card-linkedin" className="flex items-center gap-2.5 text-ink/85 hover:text-saffron transition-colors">
                  <Linkedin size={13} className="text-saffron shrink-0" />
                  linkedin.com/in/karamsodhi
                </a>
                <a href={PROFILE.websiteUrl} target="_blank" rel="noopener noreferrer" data-testid="card-website" className="flex items-center gap-2.5 text-ink/85 hover:text-saffron transition-colors">
                  <Globe size={13} className="text-saffron shrink-0" />
                  {PROFILE.website}
                </a>
                <div className="flex items-center gap-2.5 text-ink/85">
                  <MapPin size={13} className="text-saffron shrink-0" />
                  {CONTACT.city}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom accent stripe */}
          <div className="flex h-2">
            <div className="flex-1 bg-saffron" />
            <div className="flex-1 bg-magenta" />
            <div className="flex-1 bg-ink" />
          </div>
        </article>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 print:hidden">
          <button
            onClick={downloadVCard}
            data-testid="card-download-vcard"
            className="inline-flex items-center gap-2 px-6 py-3 bg-ink text-ivory text-[11px] uppercase tracking-[0.22em] hover:bg-saffron hover:text-ink transition-colors"
          >
            <Download size={14} /> Save to Contacts
          </button>
          <button
            onClick={shareCard}
            data-testid="card-share"
            className="inline-flex items-center gap-2 px-6 py-3 border border-ink/30 text-ink text-[11px] uppercase tracking-[0.22em] hover:bg-ink hover:text-ivory transition-colors"
          >
            <Share2 size={14} /> Share Card
          </button>
          <button
            onClick={() => window.print()}
            data-testid="card-print"
            className="inline-flex items-center gap-2 px-6 py-3 border border-ink/30 text-ink text-[11px] uppercase tracking-[0.22em] hover:bg-ink hover:text-ivory transition-colors"
          >
            <QrCode size={14} /> Print
          </button>
        </div>

        <p className="mt-6 text-center text-[11px] uppercase tracking-[0.22em] text-ink/45 print:hidden">
          Share this page: <span className="text-ink/70">karamsodhi.com/card</span>
        </p>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          @page { size: auto; margin: 12mm; }
        }
      `}</style>
    </div>
  );
}
